import React from "react";
import styled from "styled-components";
import { translateActivityAction } from "../../../../../components/activity-timeline/utils/activityUtils";
import { useTranslation } from "react-i18next";
import isEmpty from "lodash/isEmpty";

import { Flex } from "../../../../../components/Flex";
import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { AtiraEmpty } from "../../../../../components/AtiraEmpty";
import { AtiraCollapse } from "../../../../../components/AtiraCollapse";
import { Text } from "../../../../../components/Text";
import { ThemeType } from "../../../../../theme/Theme";
import { standardDate } from "../../../../../utils/Date";

import {
  ActionChip,
  ItemCard,
  ReportEntityItem,
  getEntityTitle,
  getKindColor,
  getKindColorKey,
  getKindIcon,
  onViewEntity,
} from "../../../../../utils/Reports";

import { ActivityTimelineCollapse } from "../../../../../components/activity-timeline/ActivityTimelineCollapse";
import { ActivityAction } from "../../../../../model/activity/types/ActivityAction.enum";
import { ActivityEntityKind } from "../../../../../model/activity/types/ActivityEntityKind.enum";
import { BaseActivity } from "../../../../../model/activity/BaseActivity";
import { useTheme } from "../../../../../ThemeContext";
import { Button } from "../../../../../components/Button";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { useNavigate } from "react-router-dom";
import { useDrawersContext } from "../../../../tasks/drawers-context";
import isNil from "lodash/isNil";

type Props = {
  data?: ReportEntityItem[];
};

/** A faint wash behind the kind icon (same technique as ReportWidget's
 * emphasized tile) instead of a solid fill, so the icon stays legible and the
 * colour reads as a category tag rather than a status. */
const KindBadge = styled(Flex)<{ colorKey: keyof ThemeType }>`
  position: relative;
  width: 2rem;
  height: 2rem;
  min-width: 2rem;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  z-index: 1;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-color: ${({ theme, colorKey }) => theme[colorKey]};
    opacity: 0.14;
    border-radius: inherit;
    z-index: -1;
  }
`;

const toActivity = (p: {
  resourceId: string;
  kind: ActivityEntityKind;
  action: ActivityAction;
  createdAt: any;
  meta: BaseActivity["meta"];
}): BaseActivity =>
  ({
    _id: `${p.resourceId}-${String(p.createdAt)}-${p.action}`,
    kind: p.kind,
    action: p.action,
    resourceId: p.resourceId as any,
    createdAt: p.createdAt as any,
    meta: p.meta || {},
  }) as BaseActivity;

export const ReportsUserSummaryGeneralTab: React.FC<Props> = ({
  data = [],
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigation = useNavigate();
  const drawersContext = useDrawersContext();

  if (isEmpty(data)) {
    return <AtiraEmpty description={t("common.empty")} />;
  }

  return (
    <AtiraCollapse
      accordion
      items={data.map((entityItem) => {
        const canView = [
          isNil(
            entityItem?.actions
              ?.find((a) => a.action === ActivityAction.MERGE)
              ?.data?.find((d) => d.meta?.MERGE?.into),
          ),
        ].every((condition) => condition === true);
        const kind = entityItem.entity?.entityKind;
        const colorKey = getKindColorKey(kind);

        return {
          key: entityItem.resourceId,
          label: (
            <ItemCard accent={getKindColor(kind, theme)}>
              <Flex justifyContent="space-between" alignItems="flex-start" gap="s">
                <Flex gap="s" alignItems="flex-start" flex={1}>
                  <KindBadge colorKey={colorKey}>
                    <AtiraIcon icon={getKindIcon(kind)} color={colorKey} />
                  </KindBadge>
                  <Flex flexDirection="column" gap="xs" flex={1}>
                    {getEntityTitle(entityItem, t)}
                  </Flex>
                </Flex>

                {canView ? (
                  <Button
                    margin="none"
                    padding="xs"
                    icon={faEye}
                    onClick={(e) => {
                      e?.stopPropagation();
                      onViewEntity(entityItem, drawersContext, navigation);
                    }}
                    backgroundColor="darkMain"
                    hoverBackgroundColor="main"
                  />
                ) : null}
              </Flex>

              <Flex
                gap="xs"
                flexWrap="wrap"
                justifyContent="space-between"
                alignItems="center"
              >
                <Flex gap="xs" flexWrap="wrap">
                  {(entityItem.actions || []).map((a: any) => (
                    <ActionChip
                      key={`${entityItem.resourceId}-${a.action}`}
                      color="black"
                    >
                      {translateActivityAction(t, a.action)} ({a.count})
                    </ActionChip>
                  ))}
                </Flex>

                <Text color="darkerSub" fontSize="s">
                  {t("common.lastActivity")}:{" "}
                  {standardDate<string>(entityItem.latestCreatedAt)}
                </Text>
              </Flex>
            </ItemCard>
          ),
          children: (
            <Flex flexDirection="column" gap="m">
              {(entityItem.actions || []).map((a: any) => (
                <Flex
                  key={`${entityItem.resourceId}-${a.action}`}
                  flexDirection="column"
                  gap="s"
                >
                  <Text fontWeight="bold">
                    {translateActivityAction(t, a.action)} ({a.count})
                  </Text>

                  {(a.data || [])
                    .slice()
                    .sort(
                      (x: any, y: any) =>
                        new Date(y.createdAt).getTime() -
                        new Date(x.createdAt).getTime(),
                    )
                    .map((evt: any) => (
                      <Flex
                        key={`${entityItem.resourceId}-${a.action}-${String(evt.createdAt)}`}
                        flexDirection="column"
                        gap="xs"
                      >
                        <Text color="darkerSub" fontSize="s">
                          {standardDate<string>(evt.createdAt)}
                        </Text>

                        <ActivityTimelineCollapse
                          activity={toActivity({
                            resourceId: entityItem.resourceId,
                            kind: entityItem.entity?.entityKind,
                            action: a.action,
                            createdAt: evt.createdAt,
                            meta: evt.meta,
                          })}
                        />
                      </Flex>
                    ))}
                </Flex>
              ))}
            </Flex>
          ),
        };
      })}
    />
  );
};
